// src/pages/commercial/CommercialTasksPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';

export default function CommercialTasksPage() {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);

    // Filter states
    const [filter, setFilter] = useState('todo'); // 'todo', 'done'

    // Form states
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        due_date: '',
        priority: 'normal', // low, normal, high
        assignee: user.id
    });

    const fetchTasks = async () => {
        setLoading(true);
        try {
            // Fetch tasks assigned to current user
            // We'll try to sort by due_date if it exists, otherwise created
            const res = await pb.collection('tasks').getFullList({
                sort: '-created',
                filter: `assignee = "${user.id}"`
            });
            setTasks(res);
        } catch (err) {
            console.error("Error fetching tasks", err);
            // Fallback: maybe 'assignee' field doesn't exist? Try 'owner'? 
            // But AdminTasksPage uses assignee. We stick to it.
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await pb.collection('tasks').create({
                ...formData,
                status: 'todo',
                assignee: user.id
            });
            setShowModal(false);
            setFormData({ title: '', description: '', due_date: '', priority: 'normal', assignee: user.id });
            fetchTasks();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la création de la tâche");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (task) => {
        const newStatus = task.status === 'done' ? 'todo' : 'done';
        try {
            await pb.collection('tasks').update(task.id, { status: newStatus });
            // Optimistic update
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la mise à jour");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette tâche ?")) return;
        try {
            await pb.collection('tasks').delete(id);
            setTasks(prev => prev.filter(t => t.id !== id));
        } catch (err) {
            alert("Erreur suppression");
        }
    };

    const filteredTasks = tasks.filter(t => {
        if (filter === 'todo') return t.status !== 'done';
        if (filter === 'done') return t.status === 'done';
        return true;
    });

    return (
        <CommercialLayout title="Mes Tâches">
            <div className="commercial-page-wrapper">
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }

                .commercial-page-wrapper .btn-primary { 
                    display: inline-flex; align-items: center; gap: 10px;
                    padding: 10px 14px; border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-weight: 500;
                }
                .commercial-page-wrapper .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
                
                .commercial-page-wrapper .task-filters {
                    display: flex; gap: 10px; margin-bottom: 20px;
                }
                .commercial-page-wrapper .filter-tab {
                    padding: 8px 16px; border-radius: 8px; cursor: pointer;
                    font-weight: 500; border: 1px solid transparent;
                    color: var(--admin-muted);
                }
                .commercial-page-wrapper .filter-tab.active {
                    background: var(--admin-panel);
                    color: var(--admin-accent);
                    border-color: var(--admin-border);
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .commercial-page-wrapper .task-item {
                    display: flex; alignItems: center; gap: 12px;
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    border-radius: 12px;
                    padding: 12px 16px;
                    margin-bottom: 12px;
                    transition: all 0.2s;
                }
                .commercial-page-wrapper .task-item:hover {
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }
                .commercial-page-wrapper .task-checkbox {
                    width: 24px; height: 24px;
                    border: 2px solid var(--admin-border);
                    border-radius: 6px;
                    cursor: pointer;
                    display: grid; place-items: center;
                    color: transparent;
                    transition: all 0.2s;
                }
                .commercial-page-wrapper .task-checkbox.checked {
                    background: var(--admin-accent);
                    border-color: var(--admin-accent);
                    color: white;
                }
                
                .commercial-page-wrapper .priority-badge {
                    padding: 2px 8px; border-radius: 99px; font-size: 0.75rem; font-weight: 600; text-transform: uppercase;
                }
                .commercial-page-wrapper .priority-high { background: rgba(239, 68, 68, 0.1); color: #ef4444; }
                .commercial-page-wrapper .priority-normal { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
                .commercial-page-wrapper .priority-low { background: rgba(100, 116, 139, 0.1); color: #64748b; }

                /* Modal */
                .commercial-page-wrapper .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; backdrop-filter: blur(2px); }
                .commercial-page-wrapper .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 16px; width: 100%; max-width: 500px; padding: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .commercial-page-wrapper .form-group { margin-bottom: 16px; }
                .commercial-page-wrapper .form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 0.9rem; }
                .commercial-page-wrapper .form-control { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); font-size: 0.95rem; }

                `}</style>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Tâches</h1>
                        <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0' }}>Organisez votre journée de travail</p>
                    </div>
                    <button className="btn-primary" onClick={() => setShowModal(true)}>
                        <span>+ Nouvelle Tâche</span>
                    </button>
                </div>

                <div className="task-filters">
                    <div className={`filter-tab ${filter === 'todo' ? 'active' : ''}`} onClick={() => setFilter('todo')}>À faire</div>
                    <div className={`filter-tab ${filter === 'done' ? 'active' : ''}`} onClick={() => setFilter('done')}>Terminé</div>
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement...</div>
                ) : (
                    <div className="task-list">
                        {filteredTasks.length === 0 ? (
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-muted)', fontStyle: 'italic', border: '1px dashed var(--admin-border)', borderRadius: 12 }}>
                                {filter === 'todo' ? 'Rien à faire... Profitez-en !' : 'Aucune tâche terminée récemment.'}
                            </div>
                        ) : (
                            filteredTasks.map(task => (
                                <div key={task.id} className="task-item">
                                    <div
                                        className={`task-checkbox ${task.status === 'done' ? 'checked' : ''}`}
                                        onClick={() => handleToggleStatus(task)}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, textDecoration: task.status === 'done' ? 'line-through' : 'none', color: task.status === 'done' ? 'var(--admin-muted)' : 'var(--admin-text)' }}>
                                            {task.title || 'Sans titre'}
                                        </div>
                                        {task.description && <div style={{ fontSize: '0.85rem', color: 'var(--admin-muted)', marginTop: 2 }}>{task.description}</div>}
                                        <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', marginTop: 6, color: 'var(--admin-muted)' }}>
                                            {task.due_date && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                                    {new Date(task.due_date).toLocaleDateString()}
                                                </span>
                                            )}
                                            {task.priority && task.priority !== 'normal' && (
                                                <span className={`priority-badge priority-${task.priority}`}>
                                                    {task.priority === 'high' ? 'Urgent' : 'Faible'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button onClick={() => handleDelete(task.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-muted)', opacity: 0.5 }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Modal New Task */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <h2 style={{ marginTop: 0, marginBottom: 20 }}>Nouvelle Tâche</h2>
                            <form onSubmit={handleCreate}>
                                <div className="form-group">
                                    <label>Titre</label>
                                    <input required className="form-control" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Relancer Prospect X" />
                                </div>
                                <div className="form-group">
                                    <label>Description (optionnel)</label>
                                    <textarea className="form-control" rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                                </div>
                                <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label>Échéance</label>
                                        <input type="date" className="form-control" value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })} />
                                    </div>
                                    <div>
                                        <label>Priorité</label>
                                        <select className="form-control" value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                                            <option value="low">Faible</option>
                                            <option value="normal">Normale</option>
                                            <option value="high">Urgente</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                                    <button type="button" onClick={() => setShowModal(false)} style={{ background: 'transparent', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
                                    <button type="submit" disabled={saving} className="btn-primary" style={{ border: 'none' }}>
                                        {saving ? '...' : 'Créer'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}
